<template>
    <!-- Email update modal -->
    <b-modal
        v-model="isVisible"
        size="lg"
        title="Email change"
        modal-class="vue-need-bootstrap"
        lazy
        centered
        hide-footer
    >
        <template #default="{ cancel, ok }">
            <div v-show="!emailChanged">
                <!-- Current Email -->
                <b-form-group
                    id="current-email-input"
                    label-cols="3"
                    content-cols="9"
                    label="Current email"
                    label-for="current-email-input"
                    class="my-3"
                >
                    <b-form-input id="current-email-input" v-model="email" type="email" disabled />
                    <b-form-text v-show="pendingEmail"
                        ><i class="fa fa-info-circle mr-1" />You have a pending confirmation for
                        <b>{{ pendingEmail }}</b
                        >.</b-form-text
                    >
                </b-form-group>

                <validation-observer v-slot="{ handleSubmit, valid }">
                    <b-form @submit.stop.prevent="handleSubmit(changeEmailAddress)">
                        <!-- New Email -->
                        <validation-provider v-slot="{ valid, errors }" rules="email|required" name="Email">
                            <b-form-group
                                id="new-email-input"
                                label-cols="3"
                                content-cols="9"
                                label="New email"
                                label-for="new-email-input"
                                class="my-3"
                                :invalid-feedback="
                                    emailsAreTheSame
                                        ? 'New email must be different'
                                        : invalidEmail
                                        ? 'Email already in use'
                                        : errors[0]
                                "
                                :state="
                                    invalidEmail || emailsAreTheSame ? false : errors[0] ? false : valid ? true : null
                                "
                            >
                                <b-form-input
                                    id="new-email-input"
                                    v-model="newEmail"
                                    type="email"
                                    :state="
                                        invalidEmail || emailsAreTheSame
                                            ? false
                                            : errors[0]
                                            ? false
                                            : valid
                                            ? true
                                            : null
                                    "
                                    :disabled="savingEmail"
                                    trim
                                />
                            </b-form-group>
                        </validation-provider>

                        <!-- Password -->
                        <validation-provider v-slot="{ valid, errors }" rules="required|min:8" name="Password">
                            <b-form-group
                                id="current-password-input"
                                label-cols="3"
                                content-cols="9"
                                label="Current password"
                                label-for="current-password-input"
                                class="my-3"
                                :invalid-feedback="invalidPassword ? 'Wrong password' : errors[0]"
                                :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                            >
                                <b-form-input
                                    id="current-password-input"
                                    v-model="currentPassword"
                                    type="password"
                                    :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                                    :disabled="savingEmail"
                                />
                            </b-form-group>
                        </validation-provider>

                        <div class="error-message w-100 pb-3 text-center">
                            {{ emailErrorMessage }}
                        </div>

                        <div class="modal-footer row pb-0">
                            <b-button type="reset" :disabled="savingEmail" @click="cancel()"> Cancel </b-button>
                            <loader-btn
                                variant="primary"
                                type="submit"
                                loading-text="Saving"
                                :loading="savingEmail"
                                :disabled="!valid || emailsAreTheSame || savingEmail"
                            >
                                Change email address
                            </loader-btn>
                        </div>
                    </b-form>
                </validation-observer>
            </div>

            <!-- Email changed confirmation -->
            <div v-show="emailChanged" class="align-items-center confirmation-message justify-content-center">
                <div>
                    A confirmation email was sent to <b>{{ newEmail }}</b
                    >. Please check your inbox and confirm your new email.
                </div>
            </div>
            <div v-show="emailChanged" class="modal-footer row pb-0">
                <b-button variant="primary" @click="ok()"> Ok </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref } from 'vue';
import { AxiosError } from 'axios';
import { getEmailConfirmation, updateEmail } from '@/api/user';
import { BModal } from 'bootstrap-vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

const props = defineProps<{
    value: boolean;
    email?: string;
}>();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
}>();

const savingEmail = ref(false);
const emailChanged = ref(false);
const newEmail: Ref<string | null> = ref(null);
const currentPassword: Ref<string | null> = ref(null);
const emailErrorMessage: Ref<string | null> = ref(null);

const pendingEmail: Ref<string | null> = ref(null);
const invalidPassword = ref(false);
const invalidEmail = ref(false);

const isVisible = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        emailErrorMessage.value = null;
        emailChanged.value = false;
        checkPendingConfirmation();
        emit('input', value);
    }
});

const emailsAreTheSame = computed(() => {
    return props.email === newEmail.value;
});

onBeforeMount(() => {
    checkPendingConfirmation();
});

const checkPendingConfirmation = async () => {
    const pendingConfirmation = await getEmailConfirmation();
    if (pendingConfirmation.PendingConfirmation && pendingConfirmation.NewEmail) {
        pendingEmail.value = pendingConfirmation.NewEmail;
    } else {
        pendingEmail.value = null;
    }
};

const changeEmailAddress = async () => {
    try {
        savingEmail.value = true;
        emailErrorMessage.value = null;
        invalidPassword.value = false;
        invalidEmail.value = false;

        if (currentPassword.value && newEmail.value) {
            await updateEmail(currentPassword.value, newEmail.value);
        }
        emailChanged.value = true;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) {
            if (axiosError.response.data.Message) {
                if (axiosError.response.data.Message === 'Invalid Password') {
                    invalidPassword.value = true;
                } else if (axiosError.response.data.Message === 'Email already in use') {
                    invalidEmail.value = true;
                } else {
                    emailErrorMessage.value = axiosError.response.data.Message;
                }
            }
        } else {
            emailErrorMessage.value = 'Fatal error';
            window.rumReportError(axiosError);
        }
    } finally {
        savingEmail.value = false;
    }
};
</script>

<style lang="scss" scoped>
.confirmation-message {
    display: flex;
    min-height: 200px;
}
</style>
